import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { type CountryCode } from 'libphonenumber-js';

import { usePortalPosition } from '@/hooks/usePortalPosition';
import {
  formatPhoneE164,
  getCountryList,
  getDefaultCountry,
  splitE164,
  type Country,
} from '@/lib/phone-utils';

import './PhoneField.css';

export type PhoneFieldProps = {
  name: string;
  label: string;
  /** Controlled value — canonical E.164 string (e.g. `+971501234567`) or `''`. */
  value: string;
  /** Receives the canonical E.164 string on commit (onBlur or country change),
   * or the raw national value if it cannot yet be parsed (so callers using
   * `react-hook-form` still see every keystroke and can run Zod refines). */
  onChange: (value: string) => void;
  /** Initial / forced country when `value` is empty. Defaults to `AE`. */
  defaultCountry?: CountryCode;
  required?: boolean;
  error?: string;
  helper?: string;
  disabled?: boolean;
  placeholder?: string;
};

/**
 * Phone input with a country prefix picker. The country selector is portaled
 * to `document.body` so it escapes the header stacking context (same pattern
 * as `LocationCombobox`).
 *
 * The committed value is always the canonical E.164 string when parseable;
 * during typing the field sends the raw national input to `onChange` so the
 * parent form keeps drift-free state (Zod can still reject invalid input
 * at submit time). On blur, the field tries to canonicalise via
 * `libphonenumber-js` and writes the E.164 string back.
 */
export const PhoneField = forwardRef<HTMLInputElement, PhoneFieldProps>(function PhoneField(
  { name, label, value, onChange, defaultCountry, required, error, helper, disabled, placeholder },
  ref,
) {
  const { i18n, t } = useTranslation();
  const generatedId = useId();
  const id = `${name}-${generatedId}`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const listboxId = `${id}-countries`;

  const initialSplit = useMemo(
    () => splitE164(value || null),
    // value is captured once at mount; updates flow through the `value` effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [country, setCountry] = useState<CountryCode>(
    initialSplit.country || defaultCountry || getDefaultCountry(),
  );
  const [national, setNational] = useState<string>(initialSplit.national);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const closePicker = useCallback(() => {
    setOpen(false);
    setActiveIdx(0);
    setSearchQuery('');
  }, []);

  const { panelStyle } = usePortalPosition({
    triggerRef,
    open,
    offset: 6,
    onTriggerOutOfView: closePicker,
  });

  // Mirror external value changes (form.reset, draft restore) back into local
  // state. Only adopt the country when the incoming value is a canonical E.164
  // (`+…`). A raw national string echoed back mid-typing (onChange forwards
  // every keystroke) carries no country, so `splitE164` would default it to AE
  // and snap the dial code back — e.g. picking +213 then typing would revert to
  // the +971 default and canonicalise the number to the wrong country. Keep the
  // user's selected country in that case and only sync the national digits.
  useEffect(() => {
    const incoming = value || '';
    if (incoming.startsWith('+')) {
      const next = splitE164(incoming);
      setCountry((prev) => (next.country !== prev ? next.country : prev));
      setNational((prev) => (next.national !== prev ? next.national : prev));
    } else {
      setNational((prev) => (incoming !== prev ? incoming : prev));
    }
  }, [value]);

  const allCountries = useMemo(() => getCountryList(i18n.language || 'en'), [i18n.language]);
  const currentCountry: Country = allCountries.find((c) => c.code === country) ?? allCountries[0];

  const filteredCountries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q === '') return allCountries;
    return allCountries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.dial.includes(q),
    );
  }, [allCountries, searchQuery]);

  useEffect(() => {
    if (activeIdx >= filteredCountries.length) {
      setActiveIdx(filteredCountries.length > 0 ? 0 : -1);
    }
  }, [filteredCountries.length, activeIdx]);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closePicker();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closePicker]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const commitValue = useCallback(
    (nextCountry: CountryCode, nextNational: string) => {
      const trimmed = nextNational.trim();
      if (trimmed === '') {
        if (value !== '') onChange('');
        return;
      }
      const e164 = formatPhoneE164(trimmed, nextCountry);
      if (e164) {
        if (value !== e164) onChange(e164);
      } else {
        // Forward the raw national value so Zod / submit-time validation can
        // surface "invalidNumber" without dropping user input on the floor.
        if (value !== trimmed) onChange(trimmed);
      }
    },
    [onChange, value],
  );

  const selectCountry = useCallback(
    (code: CountryCode) => {
      setCountry(code);
      closePicker();
      // Re-canonicalise the national digits against the new country so the
      // committed value stays in sync (e.g. switching AE → US with "501234567"
      // re-formats to +1501234567 if valid).
      commitValue(code, national);
      triggerRef.current?.focus();
    },
    [closePicker, commitValue, national],
  );

  const onNationalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setNational(next);
    // Forward keystrokes immediately so controlled callers stay in sync;
    // canonicalisation lands on blur.
    onChange(next);
  };

  const onNationalBlur = () => {
    commitValue(country, national);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        closePicker();
        triggerRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        setActiveIdx((i) =>
          filteredCountries.length === 0 ? -1 : (i + 1) % filteredCountries.length,
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIdx((i) =>
          filteredCountries.length === 0
            ? -1
            : (i - 1 + filteredCountries.length) % filteredCountries.length,
        );
        break;
      case 'Enter': {
        event.preventDefault();
        const c = filteredCountries[activeIdx];
        if (c) selectCountry(c.code);
        break;
      }
      default:
        break;
    }
  };

  const describedBy =
    [error ? errorId : null, !error && helper ? helperId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div
      ref={rootRef}
      className={`m5-field phone-field${error ? ' m5-field--error' : ''}${disabled ? ' phone-field--disabled' : ''}`}
      onKeyDown={onKeyDown}
    >
      <label className="m5-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="m5-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="m5-field__control phone-field__control">
        <button
          ref={triggerRef}
          type="button"
          className="phone-field__country-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={t('phoneField.countryLabel')}
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
        >
          <span className="phone-field__flag" aria-hidden="true">
            {currentCountry?.flag}
          </span>
          <span className="phone-field__dial">+{currentCountry?.dial}</span>
          <span className="phone-field__caret" aria-hidden="true">
            ▾
          </span>
        </button>
        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          className="m5-field__input phone-field__input"
          placeholder={placeholder}
          value={national}
          onChange={onNationalChange}
          onBlur={onNationalBlur}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          disabled={disabled}
        />
      </div>
      {error ? (
        <span id={errorId} className="m5-field__error" role="alert">
          {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="m5-field__helper">
          {helper}
        </span>
      ) : null}
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div ref={panelRef} className="phone-field__panel" style={panelStyle}>
              <div className="phone-field__search">
                <input
                  ref={searchRef}
                  type="text"
                  className="phone-field__search-input"
                  placeholder={t('phoneField.searchCountriesPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setActiveIdx(0);
                  }}
                  aria-controls={listboxId}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
              <ul id={listboxId} role="listbox" className="phone-field__listbox" tabIndex={-1}>
                {filteredCountries.length === 0 ? (
                  <li className="phone-field__empty" role="presentation">
                    {t('phoneField.noCountryFound')}
                  </li>
                ) : (
                  filteredCountries.map((c, i) => {
                    const active = i === activeIdx;
                    const selected = c.code === country;
                    return (
                      <li
                        key={c.code}
                        role="option"
                        aria-selected={selected}
                        className={[
                          'phone-field__option',
                          active ? 'phone-field__option--active' : null,
                          selected ? 'phone-field__option--selected' : null,
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onMouseEnter={() => setActiveIdx(i)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectCountry(c.code)}
                      >
                        <span className="phone-field__option-flag" aria-hidden="true">
                          {c.flag}
                        </span>
                        <span className="phone-field__option-name">{c.name}</span>
                        <span className="phone-field__option-dial">+{c.dial}</span>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
});
